import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import Text from "@ui/Text";
import ButtonIcon from "@ui/ButtonIconV2";
import { CloseLine, InformationLine } from "@ui/IconV2/react";
import { colors, spacings } from "@ui/Theme";
import NavV2 from "@ui/NavV2";
import Tab from "@ui/NavV2/Tab";
import t from "../../../../../../../../../i18n";
import SidePanelDetails from "./sidePanelDetails";
import SidePanelHistory from "./sidePanelHistory";
import type { Project } from "../../../../../../../../../api/useProjects";
import SidePanel from "../../../../components/SidePanel";
import { css } from "@linaria/core";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import {
  useEventHistories,
  type ManualHistoryGroup,
} from "../../../../../../../../../api/events/histories";
import type { ManualEvent } from "../../../../../../../../../api/useEvents";
import type { SelectedSidePanelManualFaultEvent } from "../../../../../../hooks/createGlobalStates/useSelectedSidePanelManualFaultEvent";

interface Props {
  project: Project;
  manualEvent: ManualEvent;
  tab: "fault" | "history";
  isFolded: boolean;
  setTab: (v: React.SetStateAction<SelectedSidePanelManualFaultEvent>) => void;
  closePanel: () => void;
}

const Details = ({ isFolded, project, manualEvent, closePanel, tab, setTab }: Props) => {
  const manualFaultEventsHistory = useEventHistories({
    projectId: manualEvent.projectId,
    manualFaultId: manualEvent.manualFaultId,
    manualFaultEventId: manualEvent.id,
  });
  const manualHistories = useMemo(
    () =>
      manualFaultEventsHistory.filter(
        (history): history is ManualHistoryGroup => history.kind === "manual",
      ),
    [manualFaultEventsHistory],
  );

  const manualFaultEventHistoryCount = useMemo(
    () => manualFaultEventsHistory.reduce((acc, { events }) => acc + events.length, 0),
    [manualFaultEventsHistory],
  );

  const handleTabClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    e => {
      if (e.target instanceof HTMLElement && (e.target.id === "fault" || e.target.id === "history"))
        setTab({
          type: "details",
          tab: e.target.id,
        });
    },
    [setTab],
  );

  return (
    <SidePanel
      isFolded={isFolded}
      header={
        <>
          <div className={titleSidePanel}>
            <Text type="h4">{t("project.fault_event.side_panel.title")}</Text>
            <TooltipV2
              placement="top"
              overlay={
                <OverlayWrapper
                  className={styledOverlayWrapper}
                  text={t("project.event.manual_fault.side_panel.details.hitory.tooltip.info")}
                />
              }
            >
              <span className={centerIcon}>
                <InformationLine size="m" color={colors.carbon800} />
              </span>
            </TooltipV2>
          </div>
          <ButtonIcon onClick={closePanel}>
            <CloseLine size="m" color={colors.carbon800} />
          </ButtonIcon>
          <NavV2>
            <Tab
              id="fault"
              onClick={handleTabClick}
              className={classNames({ active: tab === "fault" })}
            >
              {t("project.fault_event.side_panel.tab.fault", 1)}
            </Tab>
            <Tab
              id="history"
              onClick={handleTabClick}
              className={classNames({ active: tab === "history" })}
            >
              {t("project.event.side_panel.tab.history", manualFaultEventHistoryCount)}
            </Tab>
          </NavV2>
        </>
      }
    >
      {tab === "fault" ? <SidePanelDetails manualFaultEvent={manualEvent} /> : <></>}
      {tab === "history" ? (
        <SidePanelHistory
          projectName={project.name}
          projectTimezone={project.timezone}
          manualEvent={manualEvent}
          manualFaultEventsHistory={manualHistories}
        />
      ) : (
        <></>
      )}
    </SidePanel>
  );
};

export default Details;

//#region CSS
const titleSidePanel = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
`;
const centerIcon = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const styledOverlayWrapper = css`
  max-width: 370px;
`;
//#endregion CSS
